/**
 * Conversation List Component
 * Displays a list of conversations with loading, error, and empty states
 */

import React from 'react';
import {
  List,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Skeleton,
  Fade,
} from '@mui/material';
import {
  ChatBubbleOutline as ChatIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ConversationListItem } from './ConversationListItem';
import type { ConversationSummary } from '../../api/data-contracts';

interface ConversationListProps {
  conversations: ConversationSummary[];
  currentConversationId: string | undefined;
  isLoading: boolean;
  error: Error | null;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, title: string) => void;
  onEdit: (conversation: ConversationSummary) => void;
}

/**
 * Loading Skeleton for conversation items
 */
const ConversationSkeleton: React.FC = () => (
  <Box sx={{ px: 2, py: 1.5, mb: 1 }}>
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Skeleton variant="circular" width={20} height={20} sx={{ mt: 0.5 }} />
      <Box sx={{ flexGrow: 1 }}>
        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="50%" height={14} />
      </Box>
    </Box>
  </Box>
);

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  currentConversationId,
  isLoading,
  error,
  onSelect,
  onDelete,
  onEdit,
}) => {
  const { t } = useTranslation();

  // Loading state with skeleton
  if (isLoading) {
    return (
      <Box sx={{ p: 1 }}>
        {[1, 2, 3, 4, 5].map((index) => (
          <ConversationSkeleton key={index} />
        ))}
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            m: 2,
            p: 3,
            borderRadius: 2,
            bgcolor: 'error.lighter',
            border: 1,
            borderColor: 'error.light',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <ErrorIcon color="error" sx={{ fontSize: 40 }} />
          <Typography
            variant="body2"
            color="error.main"
            align="center"
            sx={{ fontWeight: 500 }}
          >
            {t('conversation.loadError')}
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center">
            {error.message}
          </Typography>
        </Box>
      </Fade>
    );
  }

  // Empty state
  if (conversations.length === 0) {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: 200,
          }}
        >
          <ChatIcon
            sx={{
              fontSize: 64,
              color: 'text.disabled',
              mb: 2,
              opacity: 0.3,
            }}
          />
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 500 }}
          >
            {t('conversation.noConversations')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('conversation.startNewConversation')}
          </Typography>
        </Box>
      </Fade>
    );
  }

  // List of conversations
  return (
    <Fade in timeout={300}>
      <List
        sx={{
          p: 2,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'action.hover',
            borderRadius: 4,
            '&:hover': {
              bgcolor: 'action.selected',
            },
          },
        }}
      >
        {conversations.map((conversation) => (
          <ConversationListItem
            key={conversation.id}
            conversation={conversation}
            isSelected={conversation.id === currentConversationId}
            onSelect={onSelect}
            onDelete={onDelete}
            onEdit={onEdit}
          />
        ))}
      </List>
    </Fade>
  );
};